import { DOCUMENT } from "@angular/common";
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { ValidationResult, Validator } from "@lchemy/model/validation";
import { interval } from "rxjs";
import { map, skipWhile, takeUntil } from "rxjs/operators";

import { FORM_ERROR_FORMATTER_MAP } from "@lantern/app/common/forms/form-errors-list/constants";
import { AdHocOption } from "@lantern/app/common/forms/select/shared/ad-hoc-option";

import { FieldType } from "./constants";

const NewChildFormatterMap = {
  keyAlreadyInUse: () => "This key already exists in this object.",
  invalidJson: () => "JSON must be valid"
};

@Component({
  selector: "ta-json-new-child-form",
  templateUrl: "./json-new-child-form.template.html",
  providers: [
    {
      provide: FORM_ERROR_FORMATTER_MAP,
      useValue: NewChildFormatterMap
    }
  ]
})
export class JsonNewChildFormComponent implements OnDestroy {
  @ViewChild("formCard", { static: false }) formCard;

  @Input()
  parentType: FieldType.Object | FieldType.Array;

  @Input()
  existingKeys: any[] = [];

  @Input()
  get useLastChildStyling(): boolean {
    if (this._useLastChildStyling == null) {
      return !this.otherChildrenVisible;
    }

    return this._useLastChildStyling;
  }
  set useLastChildStyling(val: boolean) {
    this._useLastChildStyling = val;
  }
  _useLastChildStyling: boolean;

  get otherChildrenVisible(): boolean {
    return this.existingKeys.length > 0;
  }

  get childIsValue(): boolean {
    return [FieldType.String, FieldType.Number, FieldType.Boolean].includes(
      this.formGroup.get("fieldType")!.value
    );
  }

  get valueInputType(): "text" | "number" | "boolean" | undefined {
    const fieldType = this.formGroup.get("fieldType")!.value;
    if (fieldType === FieldType.String) {
      return "text";
    }
    if (fieldType === FieldType.Number) {
      return "number";
    }

    if (fieldType === FieldType.Boolean) {
      return "boolean";
    }
  }

  // tslint:disable-next-line:no-output-native
  @Output()
  submit = new EventEmitter();

  @Output()
  cancel = new EventEmitter();

  private onDestroyEmitter = new EventEmitter<void>();

  FieldType = FieldType;
  fieldTypeOptions: Array<AdHocOption<string>> = Object.values(FieldType).map(
    (fieldType) => ({
      label: fieldType,
      value: fieldType
    })
  );

  forceShowErrors = false;

  formGroup = this.fb.group({
    key: this.fb.control(null),
    value: this.fb.control(null),
    fieldType: this.fb.control(FieldType.String)
  });

  validationResult: ValidationResult;
  validator = new Validator<{ key: string; value: any; fieldType: FieldType }>({
    key: {
      check: (key) => {
        if (this.parentType === FieldType.Object && key == null) {
          return { required: true };
        } else if (
          this.parentType === FieldType.Object &&
          this.existingKeys.includes(key)
        ) {
          return {
            keyAlreadyInUse: true
          };
        }
      },
      nullable: true
    },
    value: {
      check: (value, form) => {
        if (
          [
            FieldType.String,
            FieldType.Number,
            FieldType.Boolean,
            FieldType.RawJSON
          ].includes(form.fieldType) &&
          value == null
        ) {
          return { required: true };
        }

        if (
          form.fieldType === FieldType.RawJSON &&
          !this.jsonIsValid(form.value)
        ) {
          return { invalidJson: true };
        }
      },
      nullable: true
    }
  });

  cardHeight = interval(100).pipe(
    takeUntil(this.onDestroyEmitter),
    skipWhile(() => this.formCard == null),
    map(() => this.formCard.nativeElement.clientHeight)
  );

  edgeHeight = this.cardHeight.pipe(
    map((cardHeight) => {
      if (!this.useLastChildStyling) {
        return "0";
      }

      return `${cardHeight - 64}px`;
    })
  );

  private documentRef: Document;

  constructor(private fb: FormBuilder, @Inject(DOCUMENT) documentRef: any) {
    this.documentRef = documentRef;
  }

  async submitForm(): Promise<void> {
    await this.validate();

    if (this.validationResult.errors) {
      this.forceShowErrors = true;
      return;
    }

    this.submit.emit(this.formGroup.value);
    this.resetForm();
    this.scrollNewElementInToView();
  }

  private scrollNewElementInToView(): void {
    setTimeout(() => {
      const el = this.documentRef.querySelector(".ng-animating");
      if (el != null) {
        const bounding = el.getBoundingClientRect();
        const isInView =
          bounding.top >= 0 &&
          bounding.left >= 0 &&
          bounding.bottom <=
            (window.innerHeight || document.documentElement.clientHeight) &&
          bounding.right <=
            (window.innerWidth || document.documentElement.clientWidth);

        if (!isInView) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      }
    }, 200);
  }

  cancelForm(): void {
    this.cancel.emit();
    this.resetForm();
  }

  resetForm(): void {
    this.formGroup.reset();
    this.forceShowErrors = false;
  }

  private async validate(): Promise<void> {
    this.validationResult = await this.validator.validate(this.formGroup.value);
  }

  private jsonIsValid(json: string): boolean {
    try {
      JSON.parse(json);
      return true;
    } catch (e) {
      return false;
    }
  }

  ngOnDestroy(): void {
    this.onDestroyEmitter.emit();
    this.onDestroyEmitter.complete();
  }
}
